/* eslint-disable no-restricted-syntax */
import './ModernDataTable.scss';

import { Box, Flex, Image, Skeleton, Text } from '@chakra-ui/react';
import { ReactGrid } from '@silevis/reactgrid';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { AddRowCellTemplate } from 'components/AddRowCell';
import { DateCellTemplate } from 'components/DateCell';
import DeleteGroupModal from 'components/DeleteGroupModal';
import { DeleteRowCellTemplate } from 'components/DeleteRowCell';
import { DropdownCellTemplate } from 'components/DropdownCell';
import FullScreenLoader from 'components/FullScreenLoader';
import { HeaderRowTemplate } from 'components/HeaderRow';
import PlanPermission from 'container/PlanPermission';
import { HEADER_HEIGHT_WITH_NAVIGATION } from 'projectConstants';
import {
  AddIndicator,
  DeleteIndicator,
  FetchBlockOutputsV2,
  GetDependencyIndicator,
  ReorderIndicator,
  UpdateIndicator,
  getAddIndicator,
  getBaseScenario,
  getDimensionalIndicatorV2,
  getIndicatorHeaders,
  getPlannerBlock,
  getPlannerBlockMetadata,
  getPlannerBlockMetadataBlockId,
  getPlannerBlockMetadataScenarioId,
  getPlannerBlockMetadataLoading,
  handleAddIndicator,
  getPlannerModel,
  getSelectedCell,
  getBlockOutputsLoadingV2,
  getBlockOutputsErrorV2,
  handleSelectedCellLocally,
} from 'redux/PlannerModeSlice';
import {
  appendDataTableDraftRow,
  getExandSideInputStatus,
  removeDataTableDraftRowById,
} from 'redux/BuilderModeSlice';
import { UpdateBlock } from 'redux/ModelsSlice';
import addAction from 'assets/add_action_icon.png';
import getContentBasedDimensionalColumns from 'pages/BuilderModeV1/utils/contentBasedColumnWidths';
import { buildFetchBlockOutputsV2Payload } from 'pages/BuilderMode/dataTableV2/buildFetchBlockOutputsV2Payload';
import derivedRowsSyncKey from 'pages/BuilderMode/dataTableV2/derivedRowsSyncKey';
import useBuilderArrowOutputsIntegration from 'pages/BuilderMode/dataTableV2/useBuilderArrowOutputsIntegration';

import { ChevronCellTemplateOpzV2 } from './ChevronCellTemplateOpzV2';
import { ModernCheckboxCellTemplate } from './components/ModernCheckboxCell';
import SelectionActionBar from './components/SelectionActionBar';
import useDataTableRowsV2 from './dataTableV2/useDataTableRowsV2';
import {
  buildCellReference,
  getDimensionalColumns,
  getExpandedRows,
  handleCanReorderRows,
  handleCellChange,
  handleColumnResize,
  reorderArray,
} from './TableHelpersOpzV2';
import { TextCellTemplateOpzV2 } from './TextCellTemplateOpzV2';

/**
 * Same data/redux wiring as the old `BuilderModeDataTableOpzV2` (kept
 * byte-for-byte below the JSX), styled with its own self-contained
 * `ModernDataTable.scss` instead of the shared `Table.scss`. That file's
 * legacy dark/teal colours could never be reliably overridden from a second
 * stylesheet — see the comment at the top of `ModernDataTable.scss` — so
 * this table doesn't import it at all.
 */
const ModernDataTable = ({
  planPermission = false,
  contentBasedColumnWidths = false,
  hideTableAddRow = false,
}: {
  planPermission?: boolean;
  contentBasedColumnWidths?: boolean;
  hideTableAddRow?: boolean;
} = {}) => {
  const flags = useFlags();
  const { blockId } = useParams();
  const dispatch: any = useDispatch();
  const tableReference: any = useRef(null);
  const builderArrowOutputsEnabled = Boolean(flags?.builderArrowOutputs);

  // Avoid TS "union too complex" with Chakra polymorphic components
  const FlexContainer = Flex as any;
  const TextContainer = Text as any;
  const SkeletonContainer = Skeleton as any;
  const ImageContainer = Image as any;

  const selectedCell = useSelector(getSelectedCell);
  const plannerBlock = useSelector(getPlannerBlock);
  const plannerBlockMetadata = useSelector(getPlannerBlockMetadata);
  const blockMetadataScenarioId = useSelector(getPlannerBlockMetadataScenarioId);
  const blockMetadataBlockId = useSelector(getPlannerBlockMetadataBlockId);
  const blockMetadataLoading = useSelector(getPlannerBlockMetadataLoading);
  const baseScenario = useSelector(getBaseScenario);
  const addIndicator = useSelector(getAddIndicator);
  const modelDetails = useSelector(getPlannerModel);
  const indicatorHeaders = useSelector(getIndicatorHeaders);

  const indicatorValues = useSelector(getDimensionalIndicatorV2);
  const isExpand = useSelector(getExandSideInputStatus);
  const blockOutputsLoading = useSelector(getBlockOutputsLoadingV2);
  const blockOutputsError = useSelector(getBlockOutputsErrorV2);

  const metadataIndicators = plannerBlockMetadata?.indicators ?? [];

  const [deleteModal, setDeleteModal] = useState({
    indicatorId: '',
    indicatorName: '',
    status: false,
  });
  const [gridProperties, setGridProperties] = useState({});
  const [addRowWorker, setAddRowWorker] = useState<Worker | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const clickCountReference = useRef(0);
  const [isQueueProcessing, setIsQueueProcessing] = useState(false);
  const messageQueueReference = useRef<any[]>([]);
  const fetchIndicatorsRequestReference = useRef<NodeJS.Timeout>();
  const processingTimeoutReference = useRef<NodeJS.Timeout>();
  const baseScenarioReference = useRef(baseScenario);

  const hasOutputsData = (indicatorValues?.length ?? 0) > 0;
  const valuesLoading = blockOutputsLoading && !hasOutputsData;

  const { effectiveIndicatorValues, handleTableScroll, handleArrowChevronChange } =
    useBuilderArrowOutputsIntegration({
      enabled: builderArrowOutputsEnabled,
      indicatorValues,
      blockId,
      scenarioId: baseScenario?.id,
      includeFooterRow: !hideTableAddRow,
    });

  const { tableData, rows: derivedRows } = useDataTableRowsV2({
    indicatorValues: effectiveIndicatorValues,
    metadataIndicators,
    indicatorHeaders,
    modelReportingCurrency: modelDetails?.reporting_currency,
    valuesLoading,
    blockId,
    scenarioId: baseScenario?.id,
    includeFooterRow: !hideTableAddRow,
    withCheckboxColumn: true,
  });
  const memorizedColumns: any = useMemo(() => {
    if (!indicatorHeaders?.length || !plannerBlock) return [];

    if (contentBasedColumnWidths && derivedRows?.length) {
      return getContentBasedDimensionalColumns(indicatorHeaders, derivedRows);
    }

    return getDimensionalColumns(
      indicatorHeaders,
      tableReference?.current?.offsetWidth,
      plannerBlock,
      true,
    );
  }, [indicatorHeaders, plannerBlock, contentBasedColumnWidths, derivedRows, isExpand]);

  const [columns, setColumns]: any = useState(memorizedColumns);
  const [rows, setRows]: any = useState(derivedRows);
  const derivedRowsSyncKeyReference = useRef('');
  const expandedRows = useMemo(() => getExpandedRows(rows), [rows]);

  // Checkbox selection for SelectionActionBar — row data gets rebuilt
  // frequently (every output/formula change), including incrementally, so
  // the checked state lives here as a thin overlay instead of being threaded
  // through that pipeline (see `withCheckboxColumn` in TableHelpersOpzV2).
  const [checkedRowIds, setCheckedRowIds] = useState<Array<string | number>>([]);
  const selectableRowIds = useMemo(
    () =>
      expandedRows
        .filter((row: any) => row?.rowId !== 'header' && row?.rowId !== 'footer' && !row?.parentId)
        .map((row: any) => row.rowId),
    [expandedRows],
  );
  const rowsToRender = useMemo(() => {
    const selectedSet = new Set(checkedRowIds);
    return expandedRows.map((row: any) => {
      if (row?.cells?.[0]?.type !== 'checkbox') return row;
      if (row.rowId === 'header') {
        const total = selectableRowIds.length;
        const selectedCount = selectableRowIds.filter((id: any) => selectedSet.has(id)).length;
        return {
          ...row,
          cells: [
            {
              ...row.cells[0],
              checked: total > 0 && selectedCount === total,
              isIndeterminate: selectedCount > 0 && selectedCount < total,
            },
            ...row.cells.slice(1),
          ],
        };
      }
      return {
        ...row,
        cells: [{ ...row.cells[0], checked: selectedSet.has(row.rowId) }, ...row.cells.slice(1)],
      };
    });
  }, [expandedRows, checkedRowIds, selectableRowIds]);

  const createEmptyRow = useMemo(() => {
    if (!indicatorHeaders?.length) return {};

    const object: Record<string, number> = indicatorHeaders.reduce(
      (accumulator: Record<string, number>, header: any) => {
        if (header?.name) {
          accumulator[header.name] = 0;
        }
        return accumulator;
      },
      {},
    );
    return object;
  }, [indicatorHeaders]);

  useEffect(() => {
    const syncKey = derivedRowsSyncKey(derivedRows);
    if (syncKey === derivedRowsSyncKeyReference.current) {
      return;
    }
    derivedRowsSyncKeyReference.current = syncKey;
    setRows(derivedRows);
  }, [derivedRows]);

  useEffect(() => {
    if (memorizedColumns?.length >= 0) setColumns(memorizedColumns);
  }, [memorizedColumns]);

  useEffect(() => {
    if (contentBasedColumnWidths) {
      if (derivedRows?.length && indicatorHeaders?.length) {
        setColumns(getContentBasedDimensionalColumns(indicatorHeaders, derivedRows));
      }
      return;
    }

    if (tableReference?.current && indicatorHeaders?.length >= 0 && plannerBlock) {
      setColumns(
        getDimensionalColumns(
          indicatorHeaders,
          tableReference?.current?.offsetWidth,
          plannerBlock,
          true,
        ),
      );
    }
  }, [
    contentBasedColumnWidths,
    derivedRows,
    indicatorHeaders,
    plannerBlock,
    tableReference?.current,
  ]);

  const processUpdates = useCallback(
    (updates: any[]) => {
      if (updates.length === 0) return;

      requestAnimationFrame(() => {
        updates.forEach((update) => {
          dispatch(appendDataTableDraftRow(update.baseRow));
        });

        setTimeout(() => {
          const lastUpdate = updates[updates.length - 1];
          if (lastUpdate?.rowId) {
            setGridProperties({
              focusLocation: { rowId: lastUpdate.rowId, columnId: 'name' },
            });
            // "Add new" should drop straight into the name editor instead of
            // requiring an extra click/Enter once the new row is focused —
            // ReactGrid enters edit mode on Enter while a cell is focused,
            // so simulate that once the focus above has actually applied.
            setTimeout(() => {
              const enterKeyEvent = new KeyboardEvent('keydown', {
                key: 'Enter',
                code: 'Enter',
                bubbles: true,
              });
              // ReactGrid dispatches to cell templates on `event.keyCode`, and
              // that is a deprecated read-only property the constructor's init
              // dictionary silently ignores — passing it there left it 0, so
              // the grid never recognised this as Enter and stayed read-only.
              Object.defineProperty(enterKeyEvent, 'keyCode', { get: () => 13 });
              Object.defineProperty(enterKeyEvent, 'which', { get: () => 13 });
              document.activeElement?.dispatchEvent(enterKeyEvent);
            }, 50);
          }
        }, 50);

        dispatch(handleAddIndicator(false));
        setIsProcessing(false);
        clickCountReference.current = 0;
      });
    },
    [dispatch],
  );

  const processMessageQueue = useCallback(() => {
    if (messageQueueReference.current.length === 0) {
      setIsQueueProcessing(false);
      setIsProcessing(false);
      return;
    }

    const updates = [...messageQueueReference.current];
    messageQueueReference.current = [];

    processUpdates(updates);
    setIsQueueProcessing(false);
  }, [processUpdates]);

  useEffect(() => {
    const worker = new Worker(new URL('../../workers/addRowWorker.ts', import.meta.url), {
      type: 'module',
    });

    worker.addEventListener('message', (event) => {
      messageQueueReference.current.push(event.data);

      if (!isQueueProcessing) {
        setIsQueueProcessing(true);
        requestAnimationFrame(processMessageQueue);
      }
    });

    setAddRowWorker(worker);

    return () => {
      worker.terminate();
    };
  }, [isQueueProcessing, processMessageQueue]);

  const handleAddRow = useCallback(() => {
    if (!addRowWorker || isProcessing) return;

    const currentClickCount = clickCountReference.current + 1;
    clickCountReference.current = currentClickCount;
    setIsProcessing(true);

    setGridProperties({});

    if (processingTimeoutReference.current) {
      clearTimeout(processingTimeoutReference.current);
    }

    addRowWorker.postMessage({
      tableData,
      createEmptyRow,
      indicatorHeaders,
    });

    processingTimeoutReference.current = setTimeout(() => {
      if (clickCountReference.current === currentClickCount) {
        setIsProcessing(false);
      }
    }, 1000);
  }, [addRowWorker, tableData, createEmptyRow, indicatorHeaders, isProcessing]);

  useEffect(() => {
    return () => {
      if (processingTimeoutReference.current) {
        clearTimeout(processingTimeoutReference.current);
      }
    };
  }, []);

  useEffect(() => {
    if (addIndicator && addRowWorker) {
      handleAddRow();
    }
  }, [addIndicator, addRowWorker, handleAddRow]);

  useEffect(() => {
    baseScenarioReference.current = baseScenario;
  }, [baseScenario]);

  const handleDeleteRow = async (data: any) => {
    const rowToDelete = rows.find((row: any) => row.itemId === data?.id);
    if (rowToDelete?.isNewCell) {
      dispatch(removeDataTableDraftRowById(data?.id));
      return;
    }
    await dispatch(GetDependencyIndicator({ indicatorId: data?.id }));
    setDeleteModal({ indicatorId: data?.id, indicatorName: data?.itemName, status: true });
  };

  /**
   * The formula bar should behave like Excel's — always bound to whatever is
   * currently selected — so clicking the grid no longer clears the selection.
   * This handler is now a no-op kept only so the click target wiring below
   * doesn't need touching; selection changes flow entirely through
   * handleFocusLocationChanged.
   */
  const handleGridClick = () => {};

  const handleFocusLocationChanged = async (data: any) => {
    // Only header/footer re-focus is a no-op. A repeat click on a real row has
    // to fall through to the toggle below, which is what closes the formula
    // bar — this guard was returning first, so that toggle never ran.
    if (
      selectedCell?.rowId === data?.rowId &&
      (data?.rowId === 'header' || data?.rowId === 'footer')
    ) {
      return;
    }

    const sourceRow = tableData.find((item: any) => {
      const itemRowId = item?.id ?? `${item?.name}_${item?.id ?? item?.parentId}`;
      return (
        String(itemRowId) === String(data?.rowId) ||
        Number(item?.id) === Number(data?.rowId) ||
        Number(item?.indicatorId) === Number(data?.rowId)
      );
    });

    // The formula bar should always reflect the current selection (like
    // Excel's), so re-clicking the same row/cell no longer clears it — it
    // simply keeps showing that indicator.

    const cellReference = buildCellReference(columns, rowsToRender, data?.columnId, data?.rowId);

    await dispatch(
      handleSelectedCellLocally({
        ...data,
        ...(sourceRow && {}),
        id: sourceRow?.parentId ?? sourceRow?.id ?? data?.rowId,
        parentId: sourceRow?.parentId,
        indicatorId: sourceRow?.indicatorId ?? sourceRow?.parentId ?? sourceRow?.id,
        cellReference,
        cellIndicatorName: sourceRow?.name ?? '',
      }),
    );
  };

  const fetchBlockOutputsIndicators = useCallback(() => {
    const currentBaseScenario = baseScenarioReference.current;
    if (!currentBaseScenario?.id || !blockId) return;

    dispatch(
      FetchBlockOutputsV2(
        buildFetchBlockOutputsV2Payload({
          blockId,
          plannerBlock,
          scenarioId: currentBaseScenario.id,
          useArrowResponse: builderArrowOutputsEnabled,
        }),
      ),
    );
  }, [blockId, plannerBlock, dispatch, builderArrowOutputsEnabled]);

  const debouncedFetchIndicators = useCallback(() => {
    if (fetchIndicatorsRequestReference.current) {
      clearTimeout(fetchIndicatorsRequestReference.current);
    }
    fetchIndicatorsRequestReference.current = setTimeout(() => {
      fetchBlockOutputsIndicators();
    }, 2000);
  }, [fetchBlockOutputsIndicators]);

  useEffect(() => {
    return () => {
      if (fetchIndicatorsRequestReference.current) {
        clearTimeout(fetchIndicatorsRequestReference.current);
      }
    };
  }, []);

  const metadataReady =
    !blockMetadataLoading &&
    plannerBlockMetadata != null &&
    blockMetadataScenarioId === baseScenario?.id &&
    blockMetadataBlockId === Number(blockId);

  useEffect(() => {
    if (baseScenario?.id && metadataReady) {
      fetchBlockOutputsIndicators();
    }
  }, [baseScenario?.id, metadataReady, fetchBlockOutputsIndicators, blockId]);

  const handleAddDimIndicator = useCallback(
    (indicatorName: string, committedDraftRowId: number | string) => {
      dispatch(
        AddIndicator({
          blockId,
          data: {
            name: indicatorName,
            type: 'Input',
          },
          toaster: true,
          committedDraftRowId,
        }),
      ).then((response: any) => {
        if (!response?.error) {
          dispatch(handleAddIndicator(false));
          setGridProperties({
            focusLocation: { rowId: response.payload.id, columnId: 'name' },
          });
        }
      });
    },
    [blockId, dispatch],
  );

  const handleRowsReorder = async (targetRowId: any, rowIds: any[]) => {
    if (!tableData?.length) return;
    const to = tableData.findIndex((person: any) => person.id === targetRowId);
    // Only keep rows that actually exist in the flat tableData array — a
    // multi-row selection can include child/expanded or spacer rows that
    // aren't present in tableData, whose findIndex would come back -1.
    // Passing a -1 index into reorderArray resolves to `undefined` elements,
    // which previously crashed (reading `.id` of undefined) when multiple
    // rows were selected and reordered.
    const rowsIds = rowIds
      .map((id) => tableData.findIndex((person: any) => person.id === id))
      .filter((index) => index !== -1);
    if (to === -1 || rowsIds.length === 0) return;
    let reorededArray: any[];
    try {
      reorededArray = reorderArray(tableData, rowsIds, to);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to reorder indicators', error);
      return;
    }
    // Never send an empty or partial order — the endpoint rewrites the whole
    // block's indicator order from this list, so a short list would drop
    // indicators rather than reorder them.
    if (reorededArray.length === 0) return;
    dispatch(
      ReorderIndicator({
        blockId,
        data: { indicators: reorededArray },
      }),
    ).then((response: any) => {
      if (!response?.error) debouncedFetchIndicators();
    });
  };

  const updateIndicator = useCallback(
    (changes: any, change: any) => {
      dispatch(
        UpdateIndicator({
          indicatorId: changes[0]?.rowId,
          data: {
            name: change?.extraKey
              ? change?.checked
                ? 'subtotal'
                : 'default'
              : change?.checked !== undefined
              ? `${change?.checked}`
              : change?.text,
          },
          toaster: {
            errorMessage: '"An indicator with this already exists',
          },
        }),
      );
    },
    [dispatch],
  );

  const onCellChange = (changes: any, columns_: any, setRows_: any) => {
    const change = changes[0];
    if (change?.type === 'text' && change?.newCell?.escapePressed) {
      setGridProperties({});
      return;
    }
    if (change?.type === 'checkbox') {
      const checked = !!change?.newCell?.checked;
      if (change?.rowId === 'header') {
        setCheckedRowIds(checked ? [...selectableRowIds] : []);
      } else {
        setCheckedRowIds((previous) =>
          checked ? [...previous, change.rowId] : previous.filter((id) => id !== change.rowId),
        );
      }
      return;
    }
    if (change?.newCell?.isNewCell) {
      handleAddDimIndicator(change?.newCell?.text, change?.rowId);
    } else if (change?.newCell?.text !== change?.previousCell?.text) {
      updateIndicator(changes, change?.newCell);
    } else if (change?.type === 'chevron') {
      handleArrowChevronChange(change);
      handleCellChange(changes, columns_, setRows_);
    }
  };

  const onTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      handleTableScroll(event.currentTarget.scrollTop);
    },
    [handleTableScroll],
  );

  const memoizedHandleContextMenu = useCallback(
    (
      selectedRowIds: string[],
      selectedColIds: string[],
      selectionMode: string,
      menuOptions: Array<{ id: string; label: string; handler?: () => void }>,
      selectedRanges: Array<Array<{ rowId: string; columnId: string }>>,
    ) => {
      if (
        selectedRanges[0] &&
        selectedRanges[0][0]?.rowId !== 'header' &&
        selectedRanges[0][0]?.rowId !== 'footer'
      ) {
        const deleteRow = {
          id: 'deleteRow',
          label: 'Delete row',
          handler: () => {
            const myRow = rows?.filter((data2: any) => {
              return data2?.rowId === selectedRanges[0][0]?.rowId && data2;
            });
            handleDeleteRow({ id: myRow[0]?.itemId, itemName: myRow[0]?.itemName });
          },
        };
        let selectedRows = false;
        for (let index = 0; index < selectedRanges[0]?.length; index += 1) {
          selectedRows = selectedRanges[0][0]?.rowId === selectedRanges[0][index]?.rowId;
        }
        if (selectedRanges[0][0]?.columnId !== 'delete' && selectedRows) {
          const filteredOptions = menuOptions.filter((object) => object.id !== 'cut');
          return [...filteredOptions, deleteRow];
        }
        return selectedRanges[0][0]?.columnId === 'delete' && selectedRanges[0]?.length === 1
          ? [deleteRow]
          : menuOptions.filter((object) => object.id !== 'cut');
      }
      return [];
    },
    [rows, handleDeleteRow],
  );

  const memoizedHandleCellChange = useCallback(
    (changes: any) => {
      onCellChange(changes, columns, setRows);
    },
    [columns, setRows, onCellChange],
  );

  const memoizedHandleColumnResize = useCallback(
    (ci: any, width: any) => {
      handleColumnResize(ci, width, setColumns);
      setTimeout(() => {
        dispatch(
          UpdateBlock({
            blockId,
            data: { indicator_column_size: Math.floor(width) },
          }),
        );
      }, 2000);
    },
    [blockId, dispatch, setColumns],
  );

  const handleLocationChanged = () => {
    if (Object.keys(gridProperties).length > 0) {
      setGridProperties({});
    }
    return true;
  };

  useEffect(() => {
    if (Object.keys(gridProperties).length > 0) {
      setGridProperties({});
    }
  }, [rows, columns]);

  const memoizedColumns = useMemo(() => {
    return columns;
  }, [columns]);

  const handleDeleteModal = () => {
    setDeleteModal({ indicatorId: '', indicatorName: '', status: false });
    dispatch(
      DeleteIndicator({
        indicatorId: deleteModal.indicatorId,
        toaster: {
          successMessage: 'Indicator deleted',
        },
      }),
    ).then((response: any) => {
      if (!response?.error) {
        setGridProperties({
          focusLocation: { rowId: 'header', columnId: 'name' },
        });
        fetchBlockOutputsIndicators();
      }
    });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (tableReference.current && !tableReference.current.contains(target)) {
        const isMenuClick =
          target.closest('[role="menu"]') ||
          target.closest('[role="menuitem"]') ||
          target.closest('[data-menu]') ||
          target.closest('button[aria-haspopup="menu"]') ||
          target.closest('[data-chakra-menu]') ||
          target.closest('button[role="switch"]') ||
          (target.closest('input[type="checkbox"]') && target.closest('[role="menuitem"]'));

        if (!isMenuClick) {
          debouncedFetchIndicators();
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tableReference, debouncedFetchIndicators]);

  const showInitialSkeleton =
    rows?.length === 0 &&
    (blockMetadataLoading || (blockOutputsLoading && metadataIndicators.length === 0));

  if (showInitialSkeleton) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' pb='1'>
          {[...new Array(6)].map((_, index) => (
            <FlexContainer key={index} p={2} justifyContent={'space-between'}>
              {[...new Array(4)].map((__, subIndex: number) => (
                <Box key={subIndex} flex={1} mr={2}>
                  <SkeletonContainer
                    startColor='white'
                    endColor='#CCD4E8'
                    height='35px'
                    width='100%'
                  />
                </Box>
              ))}
            </FlexContainer>
          ))}
        </FlexContainer>
      </React.Fragment>
    );
  }

  if (blockOutputsError) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' alignItems='center' justifyContent='center' p={4}>
          <TextContainer color='red.500'>{blockOutputsError}</TextContainer>
        </FlexContainer>
      </React.Fragment>
    );
  }

  // If metadata exists but V2 outputs came back empty, show an explicit empty state
  // instead of rendering metadata-only placeholder rows.
  if (
    metadataIndicators.length > 0 &&
    !blockOutputsLoading &&
    !hasOutputsData &&
    (rows?.length ?? 0) === 0
  ) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' alignItems='center' justifyContent='center' p={4}>
          <TextContainer>No Data Found</TextContainer>
        </FlexContainer>
      </React.Fragment>
    );
  }

  if (!hasOutputsData && rows?.length === 0 && metadataIndicators.length === 0) {
    return (
      <React.Fragment>
        <FlexContainer direction='column' alignItems='center' justifyContent='center' p={4}>
          <Box
            onClick={() => handleAddRow()}
            width={'100%'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            flexDirection={'column'}
            gap={2}
          >
            <ImageContainer h={30} w={30} src={addAction} transform='scaleX(-1)' />
            <TextContainer
              color='#121826'
              cursor='pointer'
              textDecoration='underline'
              fontSize={'14px'}
            >
              Please click here to add indicator
            </TextContainer>
          </Box>
        </FlexContainer>
      </React.Fragment>
    );
  }

  const isTableStructureLoading =
    !columns?.length || !rowsToRender?.length || !indicatorHeaders?.length;

  if (isTableStructureLoading) {
    return (
      <React.Fragment>
        <FullScreenLoader
          width='100%'
          height={`calc(100vh - ${HEADER_HEIGHT_WITH_NAVIGATION}) - 45px`}
          bgColor='transparent'
        />
      </React.Fragment>
    );
  }

  return (
    <FlexContainer
      height={'100%'}
      width={'100%'}
      flexDirection={'column'}
      ref={tableReference}
      flex={1}
      overflow={'hidden'}
    >
      <SelectionActionBar
        selectedIds={checkedRowIds}
        onClearSelection={() => setCheckedRowIds([])}
      />
      {columns?.length &&
        rows?.length &&
        rowsToRender?.length &&
        // `columns` and `rows` update from two independent effects with
        // different dependency timing — new indicator headers can land in
        // one before the other, so there's a real transient render where
        // every row's cell count doesn't match the column count yet.
        // ReactGrid has no tolerance for that mismatch (it crashes reading
        // a column's `.width` while pairing cells to columns), so don't
        // hand it data until the two are actually in sync.
        rowsToRender.every((row: any) => row?.cells?.length === columns.length) && (
          // Bounded to the viewport (via the flex/minH chain up through
          // index.tsx's card) — a short table just renders in full, a tall one
          // scrolls internally past that point instead of growing the page.
          <Box
            className='modern-data-table-container sm-scroll'
            width={'100%'}
            margin={'initial'}
            flex='1'
            minH={0}
            overflowY='auto'
            onClick={handleGridClick}
            onScroll={onTableScroll}
          >
            <ReactGrid
              rows={rowsToRender}
              columns={memoizedColumns}
              disableVirtualScrolling={false}
              enableRowSelection={rows?.length > 3}
              stickyTopRows={rows?.length <= 6 ? 0 : 1}
              // There's no dedicated right-hand "Action" column any more (see
              // #3 — delete moved into row selection), so nothing needs to be
              // pinned to the right edge.
              stickyRightColumns={0}
              stickyLeftColumns={columns?.length <= 2 ? 0 : 1}
              stickyBottomRows={hideTableAddRow ? 0 : rows?.length <= 3 ? 0 : 1}
              verticalStickyBreakpoint={100}
              horizontalStickyBreakpoint={100}
              onRowsReordered={handleRowsReorder}
              canReorderRows={handleCanReorderRows}
              {...gridProperties}
              onFocusLocationChanging={handleLocationChanged}
              onFocusLocationChanged={handleFocusLocationChanged}
              onCellsChanged={memoizedHandleCellChange}
              onContextMenu={planPermission ? undefined : (memoizedHandleContextMenu as any)}
              onColumnResized={planPermission ? undefined : memoizedHandleColumnResize}
              customCellTemplates={{
                header: new HeaderRowTemplate(),
                delete: new DeleteRowCellTemplate(),
                text: new TextCellTemplateOpzV2(),
                date: new DateCellTemplate(),
                addRow: new AddRowCellTemplate(handleAddRow, planPermission, isProcessing),
                chevron: new ChevronCellTemplateOpzV2(planPermission),
                dropdown: new DropdownCellTemplate(handleDeleteRow, planPermission),
                checkbox: new ModernCheckboxCellTemplate(),
              }}
            />
          </Box>
        )}
      <DeleteGroupModal
        isOpen={deleteModal.status}
        onClose={() => setDeleteModal({ indicatorId: '', indicatorName: '', status: false })}
        itemName={deleteModal.indicatorName || 'some name'}
        onDelete={() => handleDeleteModal()}
        orgChart={true}
        isBuilderMode={true}
      />
    </FlexContainer>
  );
};

export default PlanPermission(ModernDataTable);
